import { useQuery, keepPreviousData } from '@tanstack/react-query';
import type { ApiError } from '@/shared/api/ApiError';
import type { Timelog } from '../domain/entities/Timelog';
import { getTimelogRepository, getEmployeeLookupRepository } from '../infrastructure/repositories/container';
import type { AttendanceEmployee } from '../infrastructure/mappers/timelogMapper';
import { timelogKeys } from './timelogKeys';
import type { CompositeTimelogRepository } from '../infrastructure/repositories/CompositeTimelogRepository';

export const useTimelogsByAccessRoleQuery = (
  accessRoleId: number | null,
  from: string,
  to: string,
) =>
  useQuery<Timelog[], ApiError>({
    queryKey: timelogKeys.byAccessRole(accessRoleId ?? 0, from, to),
    queryFn: () => {
      // T2.11: Use fetchAllWithFallback (two-flow query: API → merge → return, catch → fallback local)
      const repo = getTimelogRepository() as CompositeTimelogRepository;
      return repo.fetchAllWithFallback({
        accessRoleId: accessRoleId!,
        startDate: from,
        endDate: to,
      });
    },
    enabled: accessRoleId !== null,
    staleTime: 2 * 60_000,
    placeholderData: keepPreviousData,
    // CRITICAL: Execute query even when offline — fetchAllWithFallback handles IndexedDB fallback
    networkMode: 'always',
    // Don't retry on network errors — fetchAllWithFallback already falls back to IndexedDB
    retry: false,
  });

/** Triggered on explicit submit — not reactive typing search. */
export const useEmployeeLookupQuery = (
  searchValue: string,
  accessRoleId: number | null,
  enabled: boolean,
) =>
  useQuery<AttendanceEmployee | null, ApiError>({
    queryKey: timelogKeys.employeeLookup(accessRoleId ?? 0, searchValue),
    queryFn: () => getEmployeeLookupRepository().findByCodeOrDui(accessRoleId!, searchValue),
    enabled: enabled && searchValue.length >= 4 && accessRoleId !== null,
    staleTime: 5 * 60_000,
    // CRITICAL: Execute queryFn even when offline — composite repo handles Dexie fallback
    networkMode: 'always',
    // Composite repo returns local data or fails immediately — RQ retry adds latency with no benefit
    retry: false,
  });
